import { createContext, useContext, useEffect } from 'react'
import styled from '@emotion/styled'
import { useHistory, useParams } from 'react-router'
import { useSpring, animated } from 'react-spring'
import { BotoesTriplos } from '../components/BotoesTriplos'
import { Button } from '../components'
import Svgs from '../svgs'
import { AnimateEnter } from '../components/AnimateEnter'
import { getGaugeColorByPercent } from './utils/gaugeColor'
import aplicacaoPadrao from '../assets/marca/aplicacao_padrao.png'
import { SimuladosStore, getResultadoByAvaliacaoId, getStatusByAvaliacaoId } from './SimuladosStore'

interface ResultadoSimuladoParams {
  cursoId
  avaliacaoId
}

const META_APROVEITAMENTO = 70

type ResultadoScreenData = {
  pontuacao: number
  qtdQuestoes: number
  qtdQuestoesCorretas: number
}
type ResultadoViewState = 'loading' | 'error' | 'empty' | 'ready'
type ResultadoScreen = ReturnType<typeof useResultadoSimuladoScreen>
const ResultadoScreenContext = createContext<ResultadoScreen | null>(null)

export const ResultadoSimulado = () => {
  const { Layout, LogoNewDriver } = ResultadoSimulado
  const screen = useResultadoSimuladoScreen()
  const viewState = getResultadoViewState({
    loading: screen.status.loading,
    error: screen.status.error,
    hasData: !!screen.data,
  })

  if (viewState !== 'ready') {
    return renderEstadoResultado({
      Layout,
      viewState,
      errorMessage: screen.status.error,
      onVoltar: screen.handleVoltarSimulados,
    })
  }

  return (
    <Layout>
      <LogoNewDriver src={aplicacaoPadrao} alt="New Driver" />
      <ResultadoScreenContext.Provider value={screen}>
        <ResultadoSimuladoBody />
      </ResultadoScreenContext.Provider>
      <br />
      <br />
    </Layout>
  )
}

function ResultadoSimuladoBody() {
  return (
    <>
      <AnimateEnter delay={0}>
        <Titulo />
      </AnimateEnter>
      <AnimateEnter delay={500}>
        <Pontuacao />
      </AnimateEnter>
      <AnimateEnter delay={1000}>
        <QtdAcertos />
      </AnimateEnter>
      <AnimateEnter delay={1500}>
        <AcoesResultadoSimulado />
      </AnimateEnter>
    </>
  )
}

function useResultadoScreenContext() {
  const screen = useContext(ResultadoScreenContext)
  if (!screen) {
    throw new Error('ResultadoScreenContext não está disponível')
  }
  return screen
}

function getMensagemPorDesempenho(pontuacao: number): {
  titulo: string
  mensagem: string
  subMensagem?: string
} {
  const pontuacaoNormalizada = Math.max(0, Math.min(100, Math.round(pontuacao || 0)))

  if (pontuacaoNormalizada <= 39) {
    return {
      titulo: 'Simulado finalizado',
      mensagem: `Seu desempenho foi de ${pontuacaoNormalizada}%.`,
      subMensagem: 'Recomendamos revisar o conteúdo do módulo antes de tentar novamente.',
    }
  }

  if (pontuacaoNormalizada <= 69) {
    return {
      titulo: 'Simulado finalizado',
      mensagem: `Você fez ${pontuacaoNormalizada}%. Está próximo da meta mínima de ${META_APROVEITAMENTO}%. Revise os pontos que errou e tente novamente.`,
    }
  }

  if (pontuacaoNormalizada <= 89) {
    return {
      titulo: 'Simulado finalizado',
      mensagem: `Parabéns! Você atingiu a meta mínima de ${META_APROVEITAMENTO}% com ${pontuacaoNormalizada}% de aproveitamento.`,
    }
  }

  return {
    titulo: 'Simulado finalizado',
    mensagem: `Excelente desempenho! Você alcançou ${pontuacaoNormalizada}% de aproveitamento.`,
    subMensagem: 'Você demonstrou ótimo domínio do conteúdo.',
  }
}

function getBotoesResultadoSimulado({
  onVerGabarito,
  onVoltarSimulados,
}: {
  onVerGabarito: () => void
  onVoltarSimulados: () => void
}) {
  return [
    {
      label: 'Ver gabarito',
      icon: <Svgs.Notes height={37} width={30} color="#F06F30" />,
      onClick: onVerGabarito,
    },
    {
      label: 'Voltar aos simulados',
      icon: <Svgs.ArrowLeft />,
      onClick: onVoltarSimulados,
    },
  ]
}

function goToSimulados(history: ReturnType<typeof useHistory>, cursoId: string) {
  history.push(`/curso/${cursoId}/simulados`)
}

function useResultadoSimuladoScreen() {
  const history = useHistory()
  const { cursoId, avaliacaoId } = useParams<ResultadoSimuladoParams>()
  const dispatch = SimuladosStore.useDispatch()
  const { resultadoByAvaliacaoId, resultadoStatusByAvaliacaoId } = SimuladosStore.useState()

  const avaliacaoIdNumero = +avaliacaoId

  useEffect(() => {
    if (!avaliacaoIdNumero) return
    dispatch(SimuladosStore.thunks.loadResultado(avaliacaoIdNumero))
  }, [avaliacaoIdNumero, dispatch])

  function handleVoltarSimulados() {
    goToSimulados(history, cursoId)
  }

  function handleVerGabarito() {
    history.push(`/curso/${cursoId}/simulados/gabarito/${avaliacaoId}`)
  }

  return {
    cursoId,
    avaliacaoId,
    data: getResultadoByAvaliacaoId(resultadoByAvaliacaoId, avaliacaoIdNumero) as
      | ResultadoScreenData
      | undefined,
    status: getStatusByAvaliacaoId(resultadoStatusByAvaliacaoId, avaliacaoIdNumero),
    handleVoltarSimulados,
    handleVerGabarito,
  }
}

function getResultadoViewState({
  loading,
  error,
  hasData,
}: {
  loading: boolean
  error?: string
  hasData: boolean
}): ResultadoViewState {
  if (loading && !hasData) return 'loading'
  if (!!error && !hasData) return 'error'
  if (!hasData) return 'empty'
  return 'ready'
}

function renderEstadoResultado({
  Layout,
  viewState,
  errorMessage,
  onVoltar,
}: {
  Layout: typeof ResultadoSimulado.Layout
  viewState: Exclude<ResultadoViewState, 'ready'>
  errorMessage?: string
  onVoltar: () => void
}): JSX.Element {
  if (viewState === 'loading') {
    return (
      <Layout>
        <MensagemEstado>Carregando resultado...</MensagemEstado>
      </Layout>
    )
  }

  return (
    <Layout>
      <MensagemEstado>
        {viewState === 'error'
          ? errorMessage
          : 'Não foi possível carregar o resultado do simulado.'}
        <AcoesFallback>
          <Button label="Voltar aos simulados" onClick={onVoltar} />
        </AcoesFallback>
      </MensagemEstado>
    </Layout>
  )
}

function Titulo() {
  const screen = useResultadoScreenContext()
  const pontuacaoNumero = Math.max(0, Math.round(screen.data?.pontuacao || 0))
  const mensagemDesempenho = getMensagemPorDesempenho(pontuacaoNumero)

  return (
    <TituloContainer>
      <strong>{mensagemDesempenho.titulo}</strong>
      <div>{mensagemDesempenho.mensagem}</div>
      {mensagemDesempenho.subMensagem ? <small>{mensagemDesempenho.subMensagem}</small> : null}
    </TituloContainer>
  )
}

function Pontuacao() {
  const screen = useResultadoScreenContext()
  const pontuacaoNumero = Math.max(0, Math.round(screen.data?.pontuacao || 0))

  const springPontuacao = useSpring({
    number: pontuacaoNumero,
    from: { number: 0 },
    config: { mass: 1, tension: 380, friction: 220 },
  })

  const borderColor = getGaugeColorByPercent(pontuacaoNumero)

  return (
    <PontuacaoContainer style={{ borderColor }}>
      <animated.span>{springPontuacao.number.interpolate(Math.ceil as any)}</animated.span>
      <small>%</small>
    </PontuacaoContainer>
  )
}

function QtdAcertos() {
  const screen = useResultadoScreenContext()
  const pontuacaoNumero = Math.max(0, Math.round(screen.data?.pontuacao || 0))
  const atingiuMeta = pontuacaoNumero >= META_APROVEITAMENTO

  return (
    <QtdAcertosContainer>
      Você acertou <strong>{screen.data?.qtdQuestoesCorretas || 0}</strong> de{' '}
      <strong>{screen.data?.qtdQuestoes || 0}</strong> questões.
      {!atingiuMeta ? (
        <QtdAcertosMetaAlert>
          Continue praticando para alcançar a meta de <strong>{META_APROVEITAMENTO}%</strong>.
        </QtdAcertosMetaAlert>
      ) : null}
    </QtdAcertosContainer>
  )
}

function AcoesResultadoSimulado() {
  const screen = useResultadoScreenContext()
  const botoes = getBotoesResultadoSimulado({
    onVerGabarito: screen.handleVerGabarito,
    onVoltarSimulados: screen.handleVoltarSimulados,
  })

  return <BotoesTriplos botoes={botoes} />
}

ResultadoSimulado.Layout = styled('div')`
  overflow: auto;
  padding-bottom: 24px;
`

ResultadoSimulado.LogoNewDriver = styled.img`
  width: 134px;
  margin: 18px auto 0;
  display: block;
`

const TituloContainer = styled.div`
  font-size: 24px;
  line-height: 150%;
  text-align: center;
  color: #2d2d2d;
  margin-top: 110px;
  @media (max-width: 575px) {
    font-size: 18px;
    margin-top: 32px;
  }
`

const PontuacaoContainer = styled(animated.div)`
  border-radius: 50%;
  height: 132px;
  width: 132px;
  border: 10px solid transparent;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 36px;
  color: #333333;
  margin: 48px auto 0 auto;
  @media (max-width: 575px) {
    margin-top: 24px;
  }
`

const QtdAcertosContainer = styled.div`
  text-align: center;
  margin: 18px 0px;
`

const QtdAcertosMetaAlert = styled.div`
  margin-top: 8px;
  font-size: 15px;
  color: #2d2d2d;
`

const MensagemEstado = styled.div`
  text-align: center;
  margin-top: 64px;
  font-size: 18px;
  color: #2d2d2d;
`

const AcoesFallback = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: center;
`
